import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccounts, accountCategoryLabels } from '../hooks/useAccounts';
import { useCounterparties } from '../hooks/useCounterparties';
import {
  useCreateJournalEntry,
  taxTypeLabels,
  type JournalEntryInput,
} from '../hooks/useJournalEntries';
import type { LineType, TaxType } from '../types';

type LineDraft = {
  key: string;
  lineType: LineType;
  accountId: string;
  amount: string;
};

function newLine(lineType: LineType): LineDraft {
  return { key: crypto.randomUUID(), lineType, accountId: '', amount: '' };
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function JournalEntryNewPage() {
  const navigate = useNavigate();
  const { data: accounts } = useAccounts();
  const { data: counterparties } = useCounterparties();
  const createMut = useCreateJournalEntry();

  const [entryDate, setEntryDate] = useState(todayIso());
  const [description, setDescription] = useState('');
  const [taxType, setTaxType] = useState<TaxType>('taxable10');
  const [isTaxInclusive, setIsTaxInclusive] = useState(true);
  const [counterpartyId, setCounterpartyId] = useState('');
  const [receivedInvoiceNumber, setReceivedInvoiceNumber] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([
    newLine('debit'),
    newLine('credit'),
  ]);
  const [error, setError] = useState<string | null>(null);

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      const n = Number(l.amount);
      if (Number.isNaN(n)) continue;
      if (l.lineType === 'debit') debit += n;
      else credit += n;
    }
    return { debit, credit, balanced: debit === credit && debit > 0 };
  }, [lines]);

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const addLine = (lineType: LineType) => setLines((prev) => [...prev, newLine(lineType)]);
  const removeLine = (key: string) =>
    setLines((prev) => (prev.length > 2 ? prev.filter((l) => l.key !== key) : prev));

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!totals.balanced) {
      setError(`借方合計 ${totals.debit.toLocaleString()} と貸方合計 ${totals.credit.toLocaleString()} が一致しません`);
      return;
    }

    const linesPayload = lines.map((l) => ({
      lineType: l.lineType,
      accountId: l.accountId,
      amount: Number(l.amount),
    }));

    if (linesPayload.some((l) => !l.accountId || !Number.isFinite(l.amount) || l.amount <= 0)) {
      setError('全ての明細に勘定科目と金額（正の整数）を入力してください');
      return;
    }

    const input: JournalEntryInput = {
      entryDate,
      description,
      taxType,
      isTaxInclusive,
      counterpartyId: counterpartyId || null,
      receivedInvoiceNumber: receivedInvoiceNumber.trim() || null,
      lines: linesPayload,
    };

    try {
      await createMut.mutateAsync(input);
      navigate('/journal-entries');
    } catch (err) {
      setError(err instanceof Error ? err.message : '登録失敗');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>仕訳入力</h2>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="card">
          <div className="form-row">
            <label>取引日付 *</label>
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
          </div>
          <div className="form-row">
            <label>摘要 *</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              maxLength={500}
              placeholder="例: A社 11月分コンサル料"
            />
          </div>
          <div className="form-row">
            <label>消費税区分 *</label>
            <select value={taxType} onChange={(e) => setTaxType(e.target.value as TaxType)}>
              {Object.entries(taxTypeLabels).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>税込/税抜 *</label>
            <select
              value={isTaxInclusive ? 'inclusive' : 'exclusive'}
              onChange={(e) => setIsTaxInclusive(e.target.value === 'inclusive')}
            >
              <option value="inclusive">税込</option>
              <option value="exclusive">税抜</option>
            </select>
          </div>
          <div className="form-row">
            <label>取引先</label>
            <select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
              <option value="">（選択しない）</option>
              {counterparties?.map((cp) => (
                <option key={cp.id} value={cp.id}>
                  {cp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>受領インボイス番号</label>
            <input
              value={receivedInvoiceNumber}
              onChange={(e) => setReceivedInvoiceNumber(e.target.value)}
              pattern="^T\d{13}$"
              placeholder="T1234567890123（任意・仕入税額控除に必要）"
              maxLength={14}
            />
          </div>
        </div>

        <div className="card">
          <h3>仕訳明細（複合仕訳対応）</h3>
          <table className="lines-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>借/貸</th>
                <th>勘定科目</th>
                <th style={{ width: 160 }}>金額</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key}>
                  <td>
                    <select
                      value={l.lineType}
                      onChange={(e) => updateLine(l.key, { lineType: e.target.value as LineType })}
                    >
                      <option value="debit">借方</option>
                      <option value="credit">貸方</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={l.accountId}
                      onChange={(e) => updateLine(l.key, { accountId: e.target.value })}
                      required
                    >
                      <option value="">科目を選択</option>
                      {accounts?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}（{accountCategoryLabels[a.category]}）
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={l.amount}
                      onChange={(e) => updateLine(l.key, { amount: e.target.value })}
                      required
                      className="numeric"
                    />
                  </td>
                  <td>
                    <button type="button" onClick={() => removeLine(l.key)} disabled={lines.length <= 2}>
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2} style={{ textAlign: 'right' }}>
                  借方合計
                </th>
                <th className="numeric">{totals.debit.toLocaleString()}</th>
                <th></th>
              </tr>
              <tr>
                <th colSpan={2} style={{ textAlign: 'right' }}>
                  貸方合計
                </th>
                <th className="numeric">{totals.credit.toLocaleString()}</th>
                <th></th>
              </tr>
              <tr>
                <th colSpan={2} style={{ textAlign: 'right' }}>
                  借貸差額
                </th>
                <th
                  className="numeric"
                  style={{ color: totals.balanced ? 'var(--color-success)' : 'var(--color-danger)' }}
                >
                  {(totals.debit - totals.credit).toLocaleString()}
                </th>
                <th></th>
              </tr>
            </tfoot>
          </table>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <button type="button" onClick={() => addLine('debit')}>
              + 借方を追加
            </button>
            <button type="button" onClick={() => addLine('credit')}>
              + 貸方を追加
            </button>
          </div>
        </div>

        <div className="toolbar">
          <button type="submit" className="primary" disabled={createMut.isPending}>
            {createMut.isPending ? '登録中...' : '登録'}
          </button>
          <button type="button" onClick={() => navigate('/journal-entries')}>
            キャンセル
          </button>
        </div>
      </form>
    </div>
  );
}
